// repo/primary-care-connect/frontend/src/pages/Portal/ClinicAdmin/Dashboard/DashboardPage.tsx

import React, { useEffect, useMemo, useState } from "react";
import {
  clinicAdminApi,
  ApiError,
  FamilyDoctorListItem,
  PatientRequestsSummaryResponse,
  MeetAndGreetBlockedDoctor,
} from "../../../../api/clinicAdmin";
import { formatDateMaybe } from "../../../../utils/clinicAdminHelpers";
import { clinicAdminScopeHeaders } from "../../../../utils/portalRoleChoice";
import { Skeleton, SkeletonCard } from "../components/Skeleton";

import "./DashboardPage.css";

type TimeRangeKey = "30" | "90" | "12m" | "custom";
type BaselineKey = "all" | "unattached" | "attached";
type GroupByKey = "doctor" | "none" | "clinic" | "region";
type SourceScopeKey = "all" | "public" | "navigator";

type AttachmentsAnalyticsSeries = {
  key: string;
  label: string;
  data: number[];
};

type AttachmentsAnalyticsResponse = {
  months: string[];
  series: AttachmentsAnalyticsSeries[];
  meta?: any;
};

type CardVariant = "action" | "good" | "warn" | "danger" | "muted" | "summary";

type StatCard = {
  label: string;
  value: string;
  hint?: string;
  variant?: CardVariant;
};

type LeadsBreakdownItem = {
  key: string;
  label: string;
  count: number;
  percent: number;
};

type PatientLeadsAnalyticsResponse = {
  filters: {
    from: string | null;
    to: string | null;
    baseline: BaselineKey;
    sourceScope?: SourceScopeKey;
  };
  applied: {
    total: number;
    sex: LeadsBreakdownItem[];
    ageGroups: LeadsBreakdownItem[];
  };
  attached: {
    total: number;
    sex: LeadsBreakdownItem[];
    ageGroups: LeadsBreakdownItem[];
  };
  meta?: any;
};

function pad2(n: number) {
  return String(n).padStart(2, "0");
}

function utcYmd(d: Date) {
  return `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(
    d.getUTCDate()
  )}`;
}

function isoUtc(d: Date) {
  return d.toISOString();
}

type ResolvedDashboardRange = {
  from: string;
  toExclusive: string;
};

function resolveDashboardRange(
  timeRange: TimeRangeKey,
  customFrom: string,
  customTo: string
): { range: ResolvedDashboardRange | null; error: string | null } {
  if (timeRange !== "custom") {
    const toExclusive = new Date();
    const from = new Date(toExclusive.getTime());

    if (timeRange === "12m") {
      from.setUTCMonth(from.getUTCMonth() - 12);
    } else {
      const days = timeRange === "30" ? 30 : 90;
      from.setUTCDate(from.getUTCDate() - days);
    }

    from.setUTCHours(0, 0, 0, 0);

    return {
      range: { from: isoUtc(from), toExclusive: isoUtc(toExclusive) },
      error: null,
    };
  }

  if (!customFrom || !customTo) {
    return { range: null, error: "Enter both a From date and a To date." };
  }

  if (!/^\d{4}-\d{2}-\d{2}$/.test(customFrom) || !/^\d{4}-\d{2}-\d{2}$/.test(customTo)) {
    return { range: null, error: "Enter valid From and To dates." };
  }

  if (customFrom > customTo) {
    return { range: null, error: "From must be on or before To." };
  }

  const today = utcYmd(new Date());
  if (customTo > today) {
    return { range: null, error: "To cannot be in the future." };
  }

  const from = new Date(`${customFrom}T00:00:00.000Z`);
  const toExclusive = new Date(`${customTo}T00:00:00.000Z`);
  toExclusive.setUTCDate(toExclusive.getUTCDate() + 1);

  if (Number.isNaN(from.getTime()) || Number.isNaN(toExclusive.getTime())) {
    return { range: null, error: "Enter valid From and To dates." };
  }

  return {
    range: { from: isoUtc(from), toExclusive: isoUtc(toExclusive) },
    error: null,
  };
}

function defaultCustomRange(): { from: string; to: string } {
  const to = new Date();
  const from = new Date(to.getTime());
  from.setUTCDate(from.getUTCDate() - 90);
  return { from: utcYmd(from), to: utcYmd(to) };
}

const COLOR_PALETTE = [
  "#1669b3",
  "#2a9d8f",
  "#e76f51",
  "#8e5ea2",
  "#f4a261",
  "#3a86ff",
  "#00b4d8",
  "#6c757d",
  "#7cb518",
  "#ff006e",
];

function sourceScopeLabel(sourceScope: SourceScopeKey) {
  if (sourceScope === "public") return "Public requests";
  if (sourceScope === "navigator") return "Navigator requests";
  return "All requests";
}

function formatNumberMaybe(val: number | null | undefined): string {
  if (val === null || val === undefined || Number.isNaN(Number(val))) {
    return "Not set";
  }

  return new Intl.NumberFormat("en-CA", {
    maximumFractionDigits: 0,
  }).format(Number(val));
}

function formatDecimalMaybe(
  val: number | null | undefined,
  digits = 1,
  fallback = "Not enough data"
): string {
  if (val === null || val === undefined || Number.isNaN(Number(val))) {
    return fallback;
  }

  return new Intl.NumberFormat("en-CA", {
    minimumFractionDigits: 0,
    maximumFractionDigits: digits,
  }).format(Number(val));
}

function formatRemainingCapacity(val: number | null | undefined): string {
  if (val === null || val === undefined || Number.isNaN(Number(val))) {
    return "Capacity not set";
  }

  const n = Math.max(0, Math.trunc(Number(val)));
  return `${formatNumberMaybe(n)} space${n === 1 ? "" : "s"} remaining`;
}

function formatDaysValue(val: number | null | undefined): string {
  if (val === null || val === undefined || Number.isNaN(Number(val))) {
    return "—";
  }

  const n = Number(val);
  return `${formatDecimalMaybe(n, Number.isInteger(n) ? 0 : 1)} day${
    n === 1 ? "" : "s"
  }`;
}

function formatPaceValue(val: number | null | undefined): string {
  if (val === null || val === undefined || Number.isNaN(Number(val))) {
    return "—";
  }

  return `${formatDecimalMaybe(val, 2, "0")} / day`;
}

function formatEstimatedDays(
  estimatedDays: number | null | undefined,
  status: string | null | undefined
): string {
  if (status === "capacityNotSet") return "Set capacity first";
  if (status === "capacityReached") return "Capacity reached";
  if (status === "notEnoughPace") return "Not enough data";

  if (
    estimatedDays === null ||
    estimatedDays === undefined ||
    Number.isNaN(Number(estimatedDays))
  ) {
    return "—";
  }

  const n = Math.max(0, Math.ceil(Number(estimatedDays)));
  return `${formatNumberMaybe(n)} day${n === 1 ? "" : "s"}`;
}

function formatMeetAndGreetBlockedMessage(doctor: MeetAndGreetBlockedDoctor) {
  const name = String(doctor.doctorName || "").trim() || "This doctor";
  return `${name} has ${doctor.backlogCount} invited patients not yet attached or closed and cannot invite additional patients until the backlog is reduced.`;
}

function daysSinceMaybe(s: string | null | undefined): number | null {
  if (!s) return null;

  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return null;

  const diffMs = Date.now() - d.getTime();
  if (Number.isNaN(diffMs)) return null;

  return Math.floor(diffMs / (24 * 60 * 60 * 1000));
}

function acceptingStatusHint(
  acceptingNewPatients: boolean | null | undefined,
  acceptingStatusUpdatedAt: string | null | undefined
): string | null {
  if (acceptingNewPatients !== true) return null;

  const daysSince = daysSinceMaybe(acceptingStatusUpdatedAt);

  if (daysSince === null) {
    return "Needs confirmation.";
  }

  if (daysSince >= 30) {
    return "Overdue for confirmation.";
  }

  if (daysSince >= 29) {
    return "Needs confirmation today.";
  }

  if (daysSince >= 23) {
    const daysLeft = 30 - daysSince;
    return `Needs confirmation soon (${daysLeft} day${
      daysLeft === 1 ? "" : "s"
    } left).`;
  }

  return null;
}

async function fetchAttachmentsAnalytics(args: {
  from: string;
  toExclusive: string;
  baseline: BaselineKey;
  groupBy: GroupByKey;
  doctorIds: number[];
  sourceScope: SourceScopeKey;
}): Promise<AttachmentsAnalyticsResponse> {
  const params = new URLSearchParams();
  params.set("from", args.from);
  params.set("to", args.toExclusive);
  params.set("baseline", args.baseline);
  params.set("groupBy", args.groupBy);
  params.set("sourceScope", args.sourceScope);

  if (args.doctorIds.length > 0) {
    params.set("doctorIds", args.doctorIds.join(","));
  }

  const res = await fetch(
    `/api/portal/attachments-analytics?${params.toString()}`,
    {
      method: "GET",
      credentials: "include",
      headers: {
        ...clinicAdminScopeHeaders(),
      },
    }
  );

  if (res.ok) {
    const text = await res.text();
    if (!text) return { months: [], series: [] };
    return JSON.parse(text) as AttachmentsAnalyticsResponse;
  }

  let body: any = null;
  try {
    body = await res.json();
  } catch {
    // ignore
  }

  const msg =
    body?.error || `${res.status} ${res.statusText}` || "Request failed";
  throw new ApiError(res.status, msg, body);
}

async function fetchPatientLeadsAnalytics(args: {
  from: string;
  to: string;
  baseline: BaselineKey;
  doctorIds: number[];
  sourceScope: SourceScopeKey;
}): Promise<PatientLeadsAnalyticsResponse> {
  const params = new URLSearchParams();
  params.set("from", args.from);
  params.set("to", args.to);
  params.set("baseline", args.baseline);
  params.set("sourceScope", args.sourceScope);

  if (args.doctorIds.length > 0) {
    params.set("doctorIds", args.doctorIds.join(","));
  }

  const res = await fetch(
    `/api/portal/patient-leads-analytics?${params.toString()}`,
    {
      method: "GET",
      credentials: "include",
      headers: {
        ...clinicAdminScopeHeaders(),
      },
    }
  );

  if (res.ok) {
    const text = await res.text();

    if (!text) {
      return {
        filters: {
          from: args.from,
          to: args.to,
          baseline: args.baseline,
          sourceScope: args.sourceScope,
        },
        applied: { total: 0, sex: [], ageGroups: [] },
        attached: { total: 0, sex: [], ageGroups: [] },
      };
    }

    return JSON.parse(text) as PatientLeadsAnalyticsResponse;
  }

  let body: any = null;
  try {
    body = await res.json();
  } catch {
    // ignore
  }

  const msg =
    body?.error || `${res.status} ${res.statusText}` || "Request failed";
  throw new ApiError(res.status, msg, body);
}

type PieSeg = { label: string; percent: number; count: number; color: string };

const PIE_RADIUS = 50;
const PIE_CIRCUMFERENCE = 2 * Math.PI * PIE_RADIUS;

function PieChart(props: {
  title: string;
  total: number;
  items: LeadsBreakdownItem[];
  order: string[];
  paletteOffset?: number;
}) {
  const [hoveredLabel, setHoveredLabel] = useState<string | null>(null);

  const normalized = useMemo(() => {
    const byKey = new Map(props.items.map((x) => [x.key, x]));
    const out: PieSeg[] = [];

    let idx = props.paletteOffset || 0;

    for (const key of props.order) {
      const it = byKey.get(key);
      const pct = it?.percent ?? 0;
      const count = it?.count ?? 0;
      const label = it?.label ?? key;

      out.push({
        label,
        percent: Math.max(0, Math.min(100, Number(pct) || 0)),
        count,
        color: COLOR_PALETTE[idx % COLOR_PALETTE.length],
      });

      idx++;
    }

    const sum = out.reduce((a, b) => a + b.percent, 0);

    if (sum <= 0.0001) {
      return {
        segments: [
          {
            label: "No data",
            percent: 100,
            count: 0,
            color: "rgba(15, 80, 135, 0.18)",
          },
        ],
        isEmpty: true,
      };
    }

    const factor = 100 / sum;

    return {
      segments: out.map((s) => ({
        ...s,
        percent: Math.round(s.percent * factor * 10) / 10,
      })),
      isEmpty: false,
    };
  }, [props.items, props.order, props.paletteOffset]);

  const hoveredSeg = hoveredLabel
    ? normalized.segments.find((s) => s.label === hoveredLabel) ?? null
    : null;

  const centerTitle = hoveredSeg ? hoveredSeg.label : props.title;
  const centerValue = hoveredSeg ? hoveredSeg.count : props.total;

  const arcs = useMemo(() => {
    let offset = 0;

    return normalized.segments.map((s) => {
      const dashLen = (s.percent / 100) * PIE_CIRCUMFERENCE;
      const gap = PIE_CIRCUMFERENCE - dashLen;
      const dashOffset = -offset;
      offset += dashLen;

      return { ...s, dashLen, gap, dashOffset };
    });
  }, [normalized.segments]);

  return (
    <div className="ca-pie">
      <div
        className="ca-pie-donut-wrap"
        onMouseLeave={() => setHoveredLabel(null)}
      >
        <svg
          className="ca-pie-svg"
          viewBox="0 0 120 120"
          aria-label={`${props.title} pie chart`}
        >
          {arcs.map((a) => (
            <circle
              key={a.label}
              className={`ca-pie-arc${
                hoveredLabel && hoveredLabel !== a.label
                  ? " ca-pie-arc--dimmed"
                  : ""
              }`}
              cx="60"
              cy="60"
              r={PIE_RADIUS}
              fill="none"
              stroke={a.color}
              strokeWidth={normalized.isEmpty ? 16 : 18}
              strokeDasharray={`${a.dashLen} ${a.gap}`}
              strokeDashoffset={a.dashOffset}
              onMouseEnter={() => {
                if (!normalized.isEmpty) setHoveredLabel(a.label);
              }}
              style={{ cursor: normalized.isEmpty ? "default" : "pointer" }}
            />
          ))}
        </svg>

        <div className="ca-pie-hole">
          <div className="ca-pie-hole-title">{centerTitle}</div>
          <div className="ca-pie-hole-total">{centerValue}</div>
        </div>
      </div>

      <div>
        <div className="ca-pie-legend-title">{props.title}</div>

        <div className="ca-pie-legend">
          {normalized.segments.map((s) => (
            <div
              key={s.label}
              className={`ca-pie-legend-row${
                hoveredLabel && hoveredLabel !== s.label
                  ? " ca-pie-legend-row--dimmed"
                  : ""
              }`}
              onMouseEnter={() => {
                if (!normalized.isEmpty) setHoveredLabel(s.label);
              }}
              onMouseLeave={() => setHoveredLabel(null)}
            >
              <span
                className="ca-pie-swatch"
                style={{ background: s.color }}
                aria-hidden="true"
              />

              <div className="ca-pie-legend-text">
                <span className="ca-pie-legend-label">{s.label}</span>
                <span className="ca-pie-legend-pct">
                  {normalized.isEmpty ? "—" : `${s.percent}%`}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

type DoctorOption = { id: number; name: string };

export default function DashboardPage() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [clinicName, setClinicName] = useState<string>("");
  const [familyDoctors, setFamilyDoctors] = useState<FamilyDoctorListItem[]>([]);

  const [kpiDoctorId, setKpiDoctorId] = useState<number | "all">("all");
  const [kpiTimeRange, setKpiTimeRange] = useState<TimeRangeKey>("90");
  const [customFrom, setCustomFrom] = useState<string>("");
  const [customTo, setCustomTo] = useState<string>("");
  const [baseline, setBaseline] = useState<BaselineKey>("all");
  const [requestSourceScope, setRequestSourceScope] =
    useState<SourceScopeKey>("all");

  const [kpiLoading, setKpiLoading] = useState(false);
  const [kpiError, setKpiError] = useState<string | null>(null);
  const [kpiSummary, setKpiSummary] =
    useState<PatientRequestsSummaryResponse | null>(null);

  const [leadsLoading, setLeadsLoading] = useState(false);
  const [leadsError, setLeadsError] = useState<string | null>(null);
  const [leadsData, setLeadsData] =
    useState<PatientLeadsAnalyticsResponse | null>(null);

  const [chartLoading, setChartLoading] = useState<boolean>(false);
  const [chartError, setChartError] = useState<string | null>(null);
  const [chartLabels, setChartLabels] = useState<string[]>([]);
  const [chartSeries, setChartSeries] = useState<AttachmentsAnalyticsSeries[]>(
    []
  );

  useEffect(() => {
    let mounted = true;

    async function load() {
      try {
        setLoading(true);
        setError(null);

        const [clinic, fdsEnv] = await Promise.all([
          clinicAdminApi.getClinicProfile(),
          clinicAdminApi.listFamilyDoctors(),
        ]);

        if (!mounted) return;

        setClinicName(clinic?.clinicName || clinic?.clinic?.name || "");

        const fdItems = Array.isArray((fdsEnv as any)?.items)
          ? ((fdsEnv as any).items as FamilyDoctorListItem[])
          : [];

        setFamilyDoctors(fdItems);
      } catch (e: any) {
        if (!mounted) return;

        if (e instanceof ApiError && e.status === 403) {
          setError("You are not authorized to view this dashboard.");
        } else {
          setError(
            e?.message || e?.body?.error || "Failed to load dashboard data."
          );
        }
      } finally {
        if (mounted) setLoading(false);
      }
    }

    load();

    return () => {
      mounted = false;
    };
  }, []);

  const doctorOptions: DoctorOption[] = useMemo(() => {
    return familyDoctors
      .filter((d) => typeof d.familyDoctorId === "number" && d.familyDoctorId > 0)
      .slice()
      .sort((a, b) =>
        String(a.fullName || "").localeCompare(String(b.fullName || ""))
      )
      .map((d) => {
        const id = d.familyDoctorId as number;

        return {
          id,
          name: d.fullName || `Doctor #${id}`,
        };
      });
  }, [familyDoctors]);

  const selectedDoctor = useMemo(() => {
    if (kpiDoctorId === "all") return null;
    return familyDoctors.find((d) => d.familyDoctorId === kpiDoctorId) || null;
  }, [familyDoctors, kpiDoctorId]);

  const resolvedRange = useMemo(
    () => resolveDashboardRange(kpiTimeRange, customFrom, customTo),
    [kpiTimeRange, customFrom, customTo]
  );

  useEffect(() => {
    let mounted = true;

    async function loadSummary() {
      if (!resolvedRange.range) return;

      try {
        setKpiLoading(true);
        setKpiError(null);

        const resp = await clinicAdminApi.getPatientRequestsSummary({
          familyDoctorProfileId: kpiDoctorId === "all" ? null : kpiDoctorId,
          from: resolvedRange.range.from,
          to: resolvedRange.range.toExclusive,
          toMode: "exclusive",
          baseline,
          sourceScope: requestSourceScope,
        });

        if (!mounted) return;
        setKpiSummary(resp);
      } catch (e: any) {
        if (!mounted) return;

        setKpiError(
          e?.message || e?.body?.error || "Failed to load KPI summary."
        );
        setKpiSummary(null);
      } finally {
        if (mounted) setKpiLoading(false);
      }
    }

    if (!loading && !error) loadSummary();

    return () => {
      mounted = false;
    };
  }, [
    loading,
    error,
    kpiDoctorId,
    resolvedRange.range,
    baseline,
    requestSourceScope,
  ]);

  useEffect(() => {
    let mounted = true;

    async function loadLeads() {
      if (!resolvedRange.range) return;

      try {
        setLeadsLoading(true);
        setLeadsError(null);

        const doctorIds = kpiDoctorId === "all" ? [] : [kpiDoctorId];

        const resp = await fetchPatientLeadsAnalytics({
          from: resolvedRange.range.from,
          to: resolvedRange.range.toExclusive,
          baseline,
          doctorIds,
          sourceScope: requestSourceScope,
        });

        if (!mounted) return;
        setLeadsData(resp);
      } catch (e: any) {
        if (!mounted) return;

        setLeadsError(
          e?.message || e?.body?.error || "Failed to load lead analytics."
        );
        setLeadsData(null);
      } finally {
        if (mounted) setLeadsLoading(false);
      }
    }

    if (!loading && !error) loadLeads();

    return () => {
      mounted = false;
    };
  }, [
    loading,
    error,
    kpiDoctorId,
    resolvedRange.range,
    baseline,
    requestSourceScope,
  ]);

  useEffect(() => {
    let mounted = true;

    async function loadChart() {
      if (!resolvedRange.range) return;

      try {
        setChartLoading(true);
        setChartError(null);

        const doctorIds = kpiDoctorId === "all" ? [] : [kpiDoctorId];

        const resp = await fetchAttachmentsAnalytics({
          from: resolvedRange.range.from,
          toExclusive: resolvedRange.range.toExclusive,
          baseline,
          groupBy: "none",
          doctorIds,
          sourceScope: requestSourceScope,
        });

        if (!mounted) return;

        const labels = Array.isArray(resp.months) ? resp.months : [];
        setChartLabels(labels);

        const series = Array.isArray(resp.series) ? resp.series : [];
        const normalized = series.map((s) => ({
          key: String(s.key ?? ""),
          label: String(s.label ?? ""),
          data: Array.isArray(s.data)
            ? s.data.map((n) => (Number.isFinite(Number(n)) ? Number(n) : 0))
            : labels.map(() => 0),
        }));

        setChartSeries(normalized);
      } catch (e: any) {
        if (!mounted) return;

        setChartError(e?.message || e?.body?.error || "Failed to load chart.");
        setChartSeries([]);
        setChartLabels([]);
      } finally {
        if (mounted) setChartLoading(false);
      }
    }

    if (!loading && !error) loadChart();

    return () => {
      mounted = false;
    };
  }, [
    loading,
    error,
    kpiDoctorId,
    resolvedRange.range,
    baseline,
    requestSourceScope,
  ]);

  const byStatus = (kpiSummary?.byStatus || {}) as Record<string, number>;

  const totalRequests = kpiSummary?.totals?.total ?? 0;
  const awaitingReview = kpiSummary?.totals?.awaitingReview ?? 0;
  const meetAndGreetBacklog =
    kpiSummary?.totals?.acceptedPendingAttachment ?? 0;
  const attachmentsCompleted = kpiSummary?.totals?.attachmentsCompleted ?? 0;

  const waitlisted =
    kpiSummary?.pipeline?.waitlist ??
    byStatus["Waitlist"] ??
    byStatus["Waitlisted"] ??
    0;
  const declinedByClinic = byStatus["Declined"] ?? 0;
  const cancelledByPatient = byStatus["Cancelled"] ?? 0;
  const autoExpired = byStatus["Expired"] ?? 0;
  const acceptedElsewhere = byStatus["ClosedAcceptedElsewhere"] ?? 0;

  const requestSourceLabel = sourceScopeLabel(requestSourceScope);

  const capacity = kpiSummary?.capacity || null;
  const throughput = kpiSummary?.throughput || null;
  const forecast = kpiSummary?.forecast || null;

  const capacityUpdatedLabel = useMemo(
    () => formatDateMaybe(capacity?.remainingCapacityUpdatedAt),
    [capacity?.remainingCapacityUpdatedAt]
  );

  const remainingCapacityLabel = useMemo(
    () => formatRemainingCapacity(capacity?.remainingCapacity),
    [capacity?.remainingCapacity]
  );

  const averageTimeToAttachmentLabel = useMemo(
    () => formatDaysValue(throughput?.avgDaysToAttachment),
    [throughput?.avgDaysToAttachment]
  );

  const paceLabel = useMemo(
    () => formatPaceValue(throughput?.pacePerDay),
    [throughput?.pacePerDay]
  );

  const estimatedDaysToFullCapacityLabel = useMemo(
    () =>
      formatEstimatedDays(
        forecast?.estimatedDaysToFullCapacity,
        forecast?.forecastStatus
      ),
    [forecast?.estimatedDaysToFullCapacity, forecast?.forecastStatus]
  );

  const capacityRunwayMessage = useMemo(() => {
    if (!capacity || capacity.capacitySource === "missing") {
      return "Add intake capacity for your clinic's doctors to enable this forecast.";
    }

    if (forecast?.forecastMessage) {
      return forecast.forecastMessage;
    }

    if ((capacity.remainingCapacity ?? 0) <= 0) {
      return "Current clinic capacity is filled based on the latest completed attachments and capacity settings.";
    }

    if (!throughput?.pacePerDay || throughput.pacePerDay <= 0) {
      return "Not enough completed attachments in the selected time range to estimate when capacity may be filled.";
    }

    return "This private planning view updates as attachments are completed.";
  }, [capacity, forecast?.forecastMessage, throughput?.pacePerDay]);

  const capacityFootnote = useMemo(() => {
    if (!capacity || capacity.capacitySource === "missing") {
      return "Capacity is private and is not shown to patients.";
    }

    if (capacity.capacitySource === "override") {
      return capacityUpdatedLabel
        ? `Manual remaining-capacity override active · Updated ${capacityUpdatedLabel}`
        : "Manual remaining-capacity override active";
    }

    if (capacityUpdatedLabel) {
      return `Calculated from declared capacity and completed attachments · Updated ${capacityUpdatedLabel}`;
    }

    return "Calculated from declared capacity and completed attachments.";
  }, [capacity, capacityUpdatedLabel]);

  const capacityProgressPct = useMemo(() => {
    const declared = capacity?.declaredCapacity;
    const used = capacity?.usedCapacity;

    if (
      declared === null ||
      declared === undefined ||
      used === null ||
      used === undefined ||
      Number.isNaN(Number(declared)) ||
      Number.isNaN(Number(used)) ||
      Number(declared) <= 0
    ) {
      return null;
    }

    return Math.max(0, Math.min(100, (Number(used) / Number(declared)) * 100));
  }, [capacity?.declaredCapacity, capacity?.usedCapacity]);

  const kpiStatCards: StatCard[] = useMemo(() => {
    const v = (n: number) => (kpiLoading ? "…" : String(n));

    return [
      {
        label: "Total requests",
        value: v(totalRequests),
        hint: `${requestSourceLabel} in range`,
        variant: "summary",
      },
      {
        label: "Awaiting review",
        value: v(awaitingReview),
        hint: "New requests needing action",
        variant: "action",
      },
      {
        label: "Meet & Greet backlog",
        value: v(meetAndGreetBacklog),
        hint: "Patients invited awaiting attachment",
        variant: "warn",
      },
      {
        label: "Waitlisted",
        value: v(waitlisted),
        hint: "Patients currently on the waitlist",
        variant: "muted",
      },
      {
        label: "Attachments completed",
        value: v(attachmentsCompleted),
        hint: "Completed attachments in range",
        variant: "good",
      },
      {
        label: "Declined by clinic",
        value: v(declinedByClinic),
        hint: "Declined by your clinic",
        variant: "danger",
      },
      {
        label: "Cancelled by patient",
        value: v(cancelledByPatient),
        hint: "Patient cancelled request",
        variant: "muted",
      },
      {
        label: "Auto-expired",
        value: v(autoExpired),
        hint: "Expired automatically",
        variant: "muted",
      },
      {
        label: "Accepted elsewhere",
        value: v(acceptedElsewhere),
        hint: "Patient attached elsewhere",
        variant: "muted",
      },
    ];
  }, [
    kpiLoading,
    requestSourceLabel,
    awaitingReview,
    meetAndGreetBacklog,
    waitlisted,
    attachmentsCompleted,
    declinedByClinic,
    cancelledByPatient,
    autoExpired,
    acceptedElsewhere,
    totalRequests,
  ]);

  const chartMax = useMemo(() => {
    let max = 0;

    for (const s of chartSeries) {
      for (const v of s.data) {
        if (v > max) max = v;
      }
    }

    return max;
  }, [chartSeries]);

  const palette = COLOR_PALETTE;
  const BAR_LABEL_MIN_PCT = 12;

  const blockedMeetAndGreetDoctors =
    kpiSummary?.meetAndGreetLimits?.blockedDoctors || [];

  if (loading) {
    return (
      <div className="ca-dash-state">
        <div className="ca-dash-skeleton">
          <Skeleton width="200px" height="24px" />

          <div className="ca-dash-skeleton-grid">
            <SkeletonCard rows={2} />
            <SkeletonCard rows={2} />
            <SkeletonCard rows={2} />
            <SkeletonCard rows={2} />
          </div>

          <SkeletonCard rows={4} />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="ca-dash-state ca-dash-state--centered">
        <div className="ca-dash-error">
          <h2>Dashboard</h2>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  const applied = leadsData?.applied || { total: 0, sex: [], ageGroups: [] };
  const attached = leadsData?.attached || { total: 0, sex: [], ageGroups: [] };

  return (
    <div className="ca-dash">
      <div className="ca-dash-header">
        <h1>Dashboard</h1>
        <p>
          Clinic operations overview{clinicName ? ` — ${clinicName}` : ""}.
        </p>
      </div>

      {blockedMeetAndGreetDoctors.length > 0 ? (
        <div
          className="ca-backlog-alert"
          role="alert"
          aria-live="polite"
        >
          <div>
            <strong>
              {blockedMeetAndGreetDoctors.length === 1
                ? "Meet & Greet invitation limit reached"
                : "Meet & Greet invitation limits reached"}
            </strong>
            {blockedMeetAndGreetDoctors.length === 1 ? (
              <p>
                {formatMeetAndGreetBlockedMessage(blockedMeetAndGreetDoctors[0])}
              </p>
            ) : (
              <ul className="ca-backlog-alert-list">
                {blockedMeetAndGreetDoctors.map((doctor) => (
                  <li key={doctor.familyDoctorId}>
                    {formatMeetAndGreetBlockedMessage(doctor)}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      ) : null}

      <div className="ca-section ca-capacity-runway-section">
        <div className="ca-capacity-runway">
          <div className="ca-capacity-runway-main">
            <div className="ca-capacity-runway-eyebrow">
              Current capacity status
            </div>

            <div className="ca-capacity-runway-number">
              {kpiLoading ? "…" : remainingCapacityLabel}
            </div>

            <p className="ca-capacity-runway-message">
              {kpiLoading ? "Loading capacity…" : capacityRunwayMessage}
            </p>

            {capacityProgressPct !== null ? (
              <div
                className="ca-capacity-runway-progress"
                aria-label="Capacity progress"
              >
                <span
                  style={{
                    width: `${capacityProgressPct}%`,
                  }}
                />
              </div>
            ) : null}

            <div className="ca-capacity-runway-footnote">
              {capacityFootnote}
            </div>
          </div>
        </div>
      </div>

      <div className="ca-section ca-section--filters">
        <h2>Filters</h2>

        <div className="ca-filters">
          <label className="ca-filter">
            <span className="ca-filter-label">Request source</span>

            <select
              className="ca-filter-control"
              value={requestSourceScope}
              onChange={(e) =>
                setRequestSourceScope(e.target.value as SourceScopeKey)
              }
            >
              <option value="all">All requests</option>
              <option value="public">Public requests</option>
              <option value="navigator">Navigator requests</option>
            </select>
          </label>

          <label className="ca-filter">
            <span className="ca-filter-label">Doctor</span>

            <select
              className="ca-filter-control"
              value={kpiDoctorId === "all" ? "all" : String(kpiDoctorId)}
              onChange={(e) => {
                const v = e.target.value;
                if (v === "all") setKpiDoctorId("all");
                else setKpiDoctorId(parseInt(v, 10));
              }}
            >
              <option value="all">All doctors</option>
              {doctorOptions.map((d) => (
                <option key={d.id} value={String(d.id)}>
                  {d.name}
                </option>
              ))}
            </select>
          </label>

          <label className="ca-filter">
            <span className="ca-filter-label">Time range</span>

            <select
              className="ca-filter-control"
              value={kpiTimeRange}
              onChange={(e) => {
                const v = e.target.value as TimeRangeKey;
                if (v === "custom" && (!customFrom || !customTo)) {
                  const def = defaultCustomRange();
                  setCustomFrom(def.from);
                  setCustomTo(def.to);
                }
                setKpiTimeRange(v);
              }}
            >
              <option value="30">Last 30 days</option>
              <option value="90">Last 90 days</option>
              <option value="12m">Last 12 months</option>
              <option value="custom">Custom range</option>
            </select>
          </label>

          <label className="ca-filter">
            <span className="ca-filter-label">Baseline status</span>

            <select
              className="ca-filter-control"
              value={baseline}
              onChange={(e) => setBaseline(e.target.value as BaselineKey)}
            >
              <option value="all">All patients</option>
              <option value="unattached">Unattached only</option>
              <option value="attached">Attached</option>
            </select>
          </label>
        </div>

        {kpiTimeRange === "custom" ? (
          <div className="ca-filters ca-filters--custom">
            <label className="ca-filter">
              <span className="ca-filter-label">From</span>
              <input
                type="date"
                className="ca-filter-control"
                value={customFrom}
                max={customTo || utcYmd(new Date())}
                onChange={(e) => setCustomFrom(e.target.value)}
              />
            </label>

            <label className="ca-filter">
              <span className="ca-filter-label">To</span>
              <input
                type="date"
                className="ca-filter-control"
                value={customTo}
                max={utcYmd(new Date())}
                onChange={(e) => setCustomTo(e.target.value)}
              />
            </label>
          </div>
        ) : null}

        {kpiTimeRange === "custom" && resolvedRange.error ? (
          <div className="ca-filter-error" role="alert">
            {resolvedRange.error}
          </div>
        ) : null}

        {kpiError ? (
          <div className="ca-card-hint ca-mt-10">{kpiError}</div>
        ) : null}

        {selectedDoctor ? (
          <div className="ca-doctor-status">
            <span className="ca-doctor-status-name">
              {selectedDoctor.fullName || "—"}
            </span>

            <span className="ca-doctor-status-sep" aria-hidden="true" />

            <span
              className={`ca-doctor-status-badge${
                selectedDoctor.acceptingNewPatients
                  ? " ca-doctor-status-badge--yes"
                  : " ca-doctor-status-badge--no"
              }`}
            >
              {selectedDoctor.acceptingNewPatients
                ? "Accepting patients"
                : "Not accepting"}
            </span>

            {selectedDoctor.acceptingStatusUpdatedAt ? (
              <>
                <span className="ca-doctor-status-sep" aria-hidden="true" />
                <span className="ca-doctor-status-date">
                  Last confirmed {formatDateMaybe(selectedDoctor.acceptingStatusUpdatedAt)}
                </span>
              </>
            ) : null}

            {acceptingStatusHint(
              selectedDoctor.acceptingNewPatients,
              selectedDoctor.acceptingStatusUpdatedAt
            ) ? (
              <>
                <span className="ca-doctor-status-sep" aria-hidden="true" />
                <span className="ca-doctor-status-date">
                  {acceptingStatusHint(
                    selectedDoctor.acceptingNewPatients,
                    selectedDoctor.acceptingStatusUpdatedAt
                  )}
                </span>
              </>
            ) : null}
          </div>
        ) : null}
      </div>

      <div className="ca-section ca-perf-section">
        <h2>Performance summary</h2>
        <p className="ca-chart-subtitle ca-perf-subtitle">
          Based on the selected request source, doctor, time range, and
          baseline.
        </p>

        <div className="ca-perf-grid" aria-label="Filtered performance summary">
          <div className="ca-perf-card">
            <div className="ca-perf-label">Average time to attachment</div>
            <div className="ca-perf-value">
              {kpiLoading ? "…" : averageTimeToAttachmentLabel}
            </div>
            <div className="ca-perf-hint">Submitted → attachment completed</div>
          </div>

          <div className="ca-perf-card">
            <div className="ca-perf-label">Attachment pace</div>
            <div className="ca-perf-value">{kpiLoading ? "…" : paceLabel}</div>
            <div className="ca-perf-hint">Average patients attached per day</div>
          </div>

          <div className="ca-perf-card">
            <div className="ca-perf-label">Estimated days to full capacity</div>
            <div className="ca-perf-value">
              {kpiLoading ? "…" : estimatedDaysToFullCapacityLabel}
            </div>
            <div className="ca-perf-hint">
              Based on remaining capacity and pace
            </div>
          </div>
        </div>
      </div>

      <div className="ca-section">
        <h2>Patient Requests Overview</h2>

        <div className="ca-dash-grid ca-dash-grid-kpi">
          {kpiStatCards.map((c) => (
            <div
              className={`ca-card${c.variant ? ` ca-card--${c.variant}` : ""}`}
              key={c.label}
            >
              <div className="ca-card-label">{c.label}</div>
              <div className="ca-card-value">{c.value}</div>
              {c.hint ? <div className="ca-card-hint">{c.hint}</div> : null}
            </div>
          ))}
        </div>
      </div>

      <div className="ca-section">
        <h2>Patient leads overview</h2>

        {leadsError ? (
          <div className="ca-card-hint ca-mb-10">{leadsError}</div>
        ) : null}

        <div className="ca-leads-grid">
          <div className="ca-leads-card">
            <div className="ca-card-label">Applied</div>
            <div className="ca-leads-subtitle">
              Verified leads with requests submitted in range
            </div>

            <div className="ca-leads-stack">
              {leadsLoading ? (
                <div className="ca-leads-skeleton">
                  <Skeleton width="60%" height="14px" />
                  <Skeleton width="40%" height="14px" />
                </div>
              ) : (
                <>
                  <PieChart
                    title="Sex"
                    total={applied.total}
                    items={applied.sex}
                    order={["Male", "Female", "OtherOrUnknown"]}
                    paletteOffset={0}
                  />

                  <PieChart
                    title="Age groups"
                    total={applied.total}
                    items={applied.ageGroups}
                    order={["<1", "1-18", "19-65", ">65", "OtherOrUnknown"]}
                    paletteOffset={3}
                  />
                </>
              )}
            </div>
          </div>

          <div className="ca-leads-card">
            <div className="ca-card-label">Attached</div>
            <div className="ca-leads-subtitle">
              Verified leads with attachment completed in range
            </div>

            <div className="ca-leads-stack">
              {leadsLoading ? (
                <div className="ca-leads-skeleton">
                  <Skeleton width="60%" height="14px" />
                  <Skeleton width="40%" height="14px" />
                </div>
              ) : (
                <>
                  <PieChart
                    title="Sex"
                    total={attached.total}
                    items={attached.sex}
                    order={["Male", "Female", "OtherOrUnknown"]}
                    paletteOffset={0}
                  />

                  <PieChart
                    title="Age groups"
                    total={attached.total}
                    items={attached.ageGroups}
                    order={["<1", "1-18", "19-65", ">65", "OtherOrUnknown"]}
                    paletteOffset={3}
                  />
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="ca-section">
        <div className="ca-chart-head">
          <div>
            <h2 className="ca-chart-title">Attachments completed over time</h2>
            <p className="ca-chart-subtitle">
              Based on the date each attachment was completed. Uses the
              dashboard doctor and time range, including days with zero
              activity.
            </p>
          </div>
        </div>

        <div className="ca-chart">
          {chartLoading ? (
            <div className="ca-chart-skeleton">
              <Skeleton width="160px" height="14px" />
              <Skeleton width="100%" height="180px" borderRadius="12px" />
            </div>
          ) : chartError ? (
            <div className="ca-chart-state ca-chart-error">{chartError}</div>
          ) : chartLabels.length === 0 ? (
            <div className="ca-chart-state">
              No attachment data for this selection.
            </div>
          ) : (
            <div
              className="ca-chart-bars"
              style={{
                gridTemplateColumns: `repeat(${chartLabels.length}, minmax(16px, 1fr))`,
              }}
            >
              {chartLabels.map((label, idx) => (
                <div className="ca-chart-month" key={`${label}-${idx}`}>
                  <div className="ca-chart-month-bars">
                    {chartSeries.map((s, si) => {
                      const v = s.data[idx] || 0;
                      const hPct =
                        chartMax > 0 ? Math.round((v / chartMax) * 100) : 0;
                      const color = palette[si % palette.length];

                      const showLabel =
                        v > 0 &&
                        hPct >= BAR_LABEL_MIN_PCT &&
                        chartSeries.length <= 4 &&
                        chartLabels.length <= 31;

                      return (
                        <div
                          className="ca-chart-barwrap"
                          key={`${s.key}-${idx}`}
                        >
                          <div
                            className="ca-chart-bar"
                            style={{
                              height: `${Math.max(hPct, v > 0 ? 4 : 0)}%`,
                              backgroundColor: color,
                            }}
                            title={`${s.label}: ${v}`}
                            aria-label={`${s.label} ${label}: ${v}`}
                          >
                            {showLabel ? (
                              <div className="ca-chart-bar-label">{v}</div>
                            ) : null}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <div className="ca-chart-month-label">{label}</div>
                </div>
              ))}
            </div>
          )}

          {chartSeries.length > 0 && chartLabels.length > 0 ? (
            <div className="ca-chart-legend">
              {chartSeries.map((s, si) => {
                const color = palette[si % palette.length];

                return (
                  <div className="ca-legend-item" key={s.key}>
                    <span
                      className="ca-legend-swatch"
                      style={{ backgroundColor: color }}
                    />
                    <span className="ca-legend-label">{s.label}</span>
                  </div>
                );
              })}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}